import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Star, Clock, Package, TrendingDown, MapPin, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import Countdown from "../components/Countdown";
import OfferNotifyPrefs from "../components/OfferNotifyPrefs";
import WhatsAppPrefs from "../components/WhatsAppPrefs";

const PLAN = { free: null, basic: "Basic", pro: "Pro", enterprise: "Enterprise", premium: "Premium" };

function OfferCard({ o, onAccept }) {
  const badge = PLAN[o.supplier.plan];
  return (
    <div className="ga-card p-5 flex flex-col" data-testid={`offer-card-${o.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{o.category_label}</div>
          <div className="font-display font-bold text-lg text-slate-900 leading-tight mt-0.5">{o.title}</div>
        </div>
        {o.price_floor && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-bold" data-testid={`offer-falling-${o.id}`}><TrendingDown className="w-3 h-3" /> prezzo calante</span>}
      </div>
      <ul className="mt-3 text-sm text-slate-700 space-y-0.5">
        {o.items.map((it, i) => <li key={i} className="flex justify-between gap-2"><span className="truncate">{it.name}{it.notes ? <span className="text-slate-400"> · {it.notes}</span> : ""}</span><span className="font-mono shrink-0">{it.quantity} {it.unit}</span></li>)}
      </ul>
      {o.notes && <div className="mt-2 text-xs text-slate-500 line-clamp-2">{o.notes}</div>}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" /> {o.supplier.rating_avg > 0 ? `${o.supplier.rating_avg} (${o.supplier.rating_count})` : "nuovo fornitore"}</span>
        {badge && <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3" /> {badge}</span>}
        {o.supplier.distance_km != null && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.supplier.distance_km} km</span>}
        <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" /> {o.lots_left === 1 ? "ultimo lotto" : `${o.lots_left} lotti disponibili`}</span>
        <span>consegna in {o.delivery_time_hours}h</span>
      </div>
      <div className="mt-4 flex items-end justify-between gap-3 border-t border-slate-100 pt-3">
        <div>
          <div className="font-mono font-extrabold text-2xl text-emerald-700" data-testid={`offer-price-${o.id}`}>€ {o.current_price.toFixed(2)}</div>
          {o.price_floor && <div className="text-[10px] text-slate-500">scende fino a € {o.price_floor.toFixed(2)} alla scadenza</div>}
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> scade tra <Countdown endsAt={o.expires_at} /></div>
        </div>
        <Button onClick={() => onAccept(o)} className="ga-btn-primary rounded-full" data-testid={`offer-accept-${o.id}`}>{o.lots_left === 1 ? "Acquista ora" : "Accetta"}</Button>
      </div>
    </div>
  );
}

export default function OffersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [offers, setOffers] = useState(null);
  const [cat, setCat] = useState("");
  const [sel, setSel] = useState(null);
  const [addr, setAddr] = useState(user?.address || "");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/offers${cat ? `?category=${cat}` : ""}`).then(({ data }) => setOffers(data)).catch(() => setOffers([]));
  useEffect(() => { setOffers(null); load(); }, [cat]); // eslint-disable-line react-hooks/exhaustive-deps
  const categories = Array.from(new Map((offers || []).map(o => [o.category, o.category_label])).entries());
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${sel.id}/accept`, { delivery_address: addr, delivery_date: date || null });
      toast.success("Offerta accettata · ordine creato, procedi al pagamento");
      nav(`/ordine/${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "pending_ratings") { toast.error(d.message); nav(`/ordine/${d.first_order_id}`); return; }
      toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8" data-testid="offers-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Offerte dei fornitori</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Lotti in scadenza, prezzi in discesa</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">I fornitori della tua zona pubblicano stock da vendere subito a prezzo speciale. <b>Chi accetta per primo si aggiudica il lotto</b>: niente rilanci, niente attese. L'ordine nasce già aggiudicato, con pagamento protetto e contestazione come sempre. Il fornitore si rivela dopo l'accettazione.</p>
        </div>
        <Link to="/ristorante" className="text-sm text-slate-600 underline" data-testid="offers-back-dashboard">← Dashboard</Link>
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4"><OfferNotifyPrefs /><WhatsAppPrefs /></div>
      {categories.length > 1 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="offers-category-filter">
          <button onClick={() => setCat("")} className={`rounded-full px-3 py-1 text-xs font-semibold border ${!cat ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-50"}`}>Tutte</button>
          {categories.map(([id, label]) => <button key={id} onClick={() => setCat(id)} className={`rounded-full px-3 py-1 text-xs font-semibold border ${cat === id ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-50"}`} data-testid={`offers-cat-${id}`}>{label}</button>)}
        </div>
      )}
      {offers === null && <div className="mt-8 text-sm text-slate-500">Caricamento...</div>}
      {offers?.length === 0 && <div className="mt-8 ga-card p-10 text-center" data-testid="offers-empty"><Tag className="w-8 h-8 mx-auto text-slate-300" /><div className="mt-3 font-semibold text-slate-900">Nessuna offerta attiva al momento</div><div className="text-sm text-slate-500 mt-1">Riceverai una notifica quando un fornitore della tua zona pubblica un lotto nelle categorie che ordini.</div></div>}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(offers || []).map(o => <OfferCard key={o.id} o={o} onAccept={setSel} />)}
      </div>
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="offer-accept-dialog">
          <DialogHeader><DialogTitle>Accetta offerta</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm"><div className="font-semibold text-slate-900">{sel.title}</div><div className="font-mono font-bold text-emerald-700 text-xl mt-1">€ {sel.current_price.toFixed(2)}</div><div className="text-xs text-slate-500">1 lotto · consegna entro {sel.delivery_time_hours}h dal pagamento</div></div>
              <div><Label>Indirizzo di consegna</Label><Input required value={addr} onChange={e => setAddr(e.target.value)} data-testid="offer-accept-address" /></div>
              <div><Label>Data consegna desiderata (opzionale)</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="offer-accept-date" /></div>
              <Button onClick={accept} disabled={busy || addr.trim().length < 5} className="w-full ga-btn-primary rounded-full" data-testid="offer-accept-confirm">{busy ? "Creazione ordine..." : `Conferma · € ${sel.current_price.toFixed(2)}`}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
